import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { usePaymentInputs } from 'react-payment-inputs';
import images from 'react-payment-inputs/images';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { processCardInfo } from '../../../containers/processCardInfo/processCardInfoSlice';
import { encryptCheckout } from '../../../utils/encryptionUtility';

import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material';

import LoadingButton from '@mui/lab/LoadingButton';

const StyledInput = styled('div')(({ theme }) => ({
	margin: '10px',
	input: {
		width: '100%',
		padding: '10px',
		borderRadius: '5px',
		border: `1px solid ${theme.palette.common.gray}`,
		fontSize: '14px'
	},
	'.alert': {
		margin: 0,
		padding: 0,
		fontSize: '12px',
		color: theme.palette.error.main
	}
}));

const StyledSVG = styled('svg')(() => ({
	width: '150px',
	height: '100px'
}));

const Home = ({ t }) => {
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const { getCardNumberProps, getExpiryDateProps, getCardImageProps } =
		usePaymentInputs();

	const [formData, setFormData] = React.useState({
		cardNumber: '',
		expiryDate: '',
		nameOnCard: '',
		typeOfCard: ''
	});
	const [errors, setErrors] = React.useState({
		cardNumber: '',
		expiryDate: '',
		nameOnCard: '',
		customerNumber: '',
		recordingId: '',
		userId: ''
	});

	const [validationErr, setValidationErr] = React.useState(false);

	const customerNumber = Number(searchParams.get('customerNumber'));
	const recordingId = Number(searchParams.get('recordingId'));
	const userId = Number(searchParams.get('userId'));

	const { data, isLoading, hasCompleted, error } = useSelector(
		(state) => state.processCardInfoSlice.processCardInfo
	);

	const validateForm = () => {
		let valid = true;
		const newErrors = { ...errors };

		if (!formData.cardNumber.trim()) {
			newErrors.cardNumber = t('home:cardRequired');
			valid = false;
		} else {
			newErrors.cardNumber = '';
		}

		if (!formData.expiryDate.trim()) {
			newErrors.expiryDate = t('home:expiryDateRequired');
			valid = false;
		} else {
			newErrors.expiryDate = '';
		}

		if (!formData.nameOnCard.trim()) {
			newErrors.nameOnCard = t('home:nameRequired');
			valid = false;
		} else {
			newErrors.nameOnCard = '';
		}

		if (!recordingId || !customerNumber || !userId) {
			setValidationErr(true);
			valid = false;
		} else {
			setValidationErr(false);
			valid = true;
		}

		setErrors(newErrors);
		return valid;
	};

	formData.typeOfCard = getCardImageProps({ images })['aria-label'];

	const isFormEmpty =
		!formData.cardNumber.trim() ||
		!formData.expiryDate.trim() ||
		!formData.nameOnCard.trim();

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		setFormData({
			...formData,
			[name]: value
		});
	};

	const handleSubmit = () => {
		if (validateForm()) {
			const { cardNumber, expiryDate, nameOnCard, typeOfCard } = formData;
			const unformattedCardNumber = cardNumber.replace(/[ ]/g, '');
			const unformattedExpireDate = expiryDate.replace(/[ /]/g, '');
			const payload = {
				cardInfo: encryptCheckout({
					cardNumber: unformattedCardNumber,
					expiryDate: unformattedExpireDate
				}),
				customerNumber,
				nameOnCard,
				recordingId,
				userId,
				typeOfCard: typeOfCard[0].toUpperCase()
			};
			dispatch(processCardInfo(payload));
		}
	};

	return (
		<Paper
			sx={{
				padding: '15px',
				backgroundColor: (theme) => theme.palette.common.white
			}}
		>
			<Typography component='h2' variant='h6' mb={2}>
				{t('home:headerText')}
			</Typography>

			<Stack
				spacing={5}
				direction='row'
				justifyContent='space-between'
				alignItems='start'
			>
				<Stack>
					<Stack>
						<StyledSVG {...getCardImageProps({ images })} />
						<Typography textAlign='center'>{formData.typeOfCard}</Typography>
					</Stack>
					<Stack mt={3}>
						<Typography fontSize='small'>
							Recording Id:{' '}
							<Typography component='span' fontSize='small' fontWeight='bold'>
								{recordingId}
							</Typography>
						</Typography>
						<Typography fontSize='small'>
							Customer #:{' '}
							<Typography component='span' fontSize='small' fontWeight='bold'>
								{customerNumber}
							</Typography>
						</Typography>
						<Typography fontSize='small'>
							User Id:{' '}
							<Typography component='span' fontSize='small' fontWeight='bold'>
								{userId || 'N/A'}
							</Typography>
						</Typography>
					</Stack>
				</Stack>

				<Stack
					flexGrow={1}
					alignItems='center'
					justifyContent='space-between'
					sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}
				>
					<Stack direction='row' alignItems='center' gap={3}>
						<Typography
							color={(theme) => theme.palette.error.main}
							textAlign='right'
							fontSize='0.8em'
						>
							{t('home:allFieldsRequired')}
						</Typography>
						{hasCompleted && data?.statusCode === 200 && (
							<Alert severity='success'>{t('home:successMessage')}</Alert>
						)}
						{validationErr && (
							<Alert severity='error'>{t('home:unknownInfo')}</Alert>
						)}
						{(error ||
							data?.statusCode === 204 ||
							data?.statusCode === 500) && (
							<Alert severity='error'>{t('home:error')}</Alert>
						)}
					</Stack>
					<StyledInput style={{ width: '100%' }}>
						<label htmlFor='nameOnCard'>
							<Typography fontSize='0.8em'>{t('home:nameOnCard')}*</Typography>
						</label>
						<input
							onChange={handleInputChange}
							name='nameOnCard'
							id='nameOnCard'
							aria-label={t('home:nameOnCard')}
							placeholder={t('home:nameOnCard')}
						/>
						{errors.nameOnCard !== '' && (
							<p className='alert' role='alert'>
								{errors.nameOnCard}
							</p>
						)}
					</StyledInput>
					<StyledInput style={{ flex: 1 }}>
						<label htmlFor='cardNumber'>
							<Typography fontSize='0.8em'>{t('home:cardNumber')}*</Typography>
						</label>
						<input
							name='cardNumber'
							{...getCardNumberProps({
								onChange: handleInputChange
							})}
						/>
						{errors.cardNumber !== '' && (
							<p className='alert' role='alert'>
								{errors.cardNumber}
							</p>
						)}
					</StyledInput>
					<StyledInput style={{ width: '25%' }}>
						<label htmlFor='expiryDate'>
							<Typography fontSize='0.8em'>{t('home:expiryDate')}*</Typography>
						</label>
						<input
							name='expiryDate'
							{...getExpiryDateProps({
								onChange: handleInputChange
							})}
						/>
						{errors.expiryDate !== '' && (
							<p className='alert' role='alert'>
								{errors.expiryDate}
							</p>
						)}
					</StyledInput>

					<LoadingButton
						variant='contained'
						type='submit'
						color='primary'
						onClick={handleSubmit}
						loading={isLoading}
						disabled={data?.statusCode === 200 || validationErr || isFormEmpty}
						sx={{ width: '100%', margin: '0 10px' }}
					>
						{t('home:submit')}
					</LoadingButton>
				</Stack>
			</Stack>
		</Paper>
	);
};

Home.propTypes = {
	t: PropTypes.func
};

export default withTranslation('home')(Home);
